import React from "react";
import { money, fileUrl } from "@/lib/api";
import { OnyxLogo, OnyxWordmark } from "@/components/site/Brand";

const DEFAULT_TERMS = [
  { title: "Schedule", body: "Based on standard 8-hour workdays, continuous workflow without delays, and material availability." },
  { title: "Delays", body: "Time may be adjusted if weather conditions, site conditions, or access difficulties cause delays." },
  { title: "Payment Terms", body: "Per the agreed payment schedule. Retainage released upon closeout." },
  { title: "Change Orders", body: "Any changes to scope, dimensions, or conditions will require a written change order." },
  { title: "Warranty", body: "1 year workmanship warranty on labor from substantial completion." },
  { title: "Materials", body: "This proposal assumes required materials are provided by others and available on site when scheduled work begins." },
  { title: "Safety", body: "All work will be performed in accordance with OSHA standards and site safety requirements." },
  { title: "Acceptance", body: "This proposal is valid for 30 days from the date above." },
];

const RunHead = ({ company, num }) => (
  <div className="flex justify-between text-[9px] tracking-[0.15em] uppercase text-onyx-metal pb-3.5 border-b border-black/10 mb-7">
    <span>{company}</span><span>Proposal {num}</span>
  </div>
);

const SNum = ({ n, title }) => (
  <div className="flex items-center gap-4 mb-7">
    <span className="font-display font-extrabold text-4xl">{n}</span>
    <span className="text-3xl text-black/20">/</span>
    <span className="font-display font-semibold text-xl tracking-[0.15em] uppercase">{title}</span>
  </div>
);

const SubK = ({ children }) => <p className="text-[10px] tracking-[0.2em] uppercase text-onyx-metal mb-3">{children}</p>;

const Dash = ({ items }) => (
  items && items.length ? (
    <ul className="space-y-0">
      {items.filter(Boolean).map((x, i) => (
        <li key={i} className="relative pl-5 py-1.5 text-sm leading-relaxed border-b border-black/5 before:content-['—'] before:absolute before:left-0 before:text-onyx-metal">{x}</li>
      ))}
    </ul>
  ) : <p className="text-onyx-metal text-sm">—</p>
);

const Panel = ({ children }) => (
  <div className="bg-white px-8 md:px-14 py-12 border-t border-black/5">{children}</div>
);

export const ProposalDocument = ({ proposal: p, settings: s = {} }) => {
  if (!p) return null;
  const cust = p.customer || {};
  const company = s.company_name || "ONYX CONTRACTING, LLC";
  const num = p.proposal_number || "—";
  const logo = fileUrl(p.logo || s.logo || "");
  const labor = p.labor_total || p.subtotal || 0;
  const terms = (p.terms_items && p.terms_items.length ? p.terms_items : DEFAULT_TERMS);
  const quantities = p.quantities || [];
  const ourWork = p.our_work || [];

  return (
    <div className="bg-white text-onyx-charcoal mx-auto" style={{ maxWidth: 900 }} data-testid="proposal-document">
      {/* 01 · Cover */}
      <div className="print-page">
        <div className="relative h-72 md:h-[440px] bg-onyx-charcoal overflow-hidden">
          {p.cover_image && <img src={fileUrl(p.cover_image)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute top-6 right-6 md:top-9 md:right-12 text-right" style={{ filter: "drop-shadow(0 2px 12px rgba(255,255,255,0.55))" }}>
            {logo ? <img src={logo} alt="ONYX CONTRACTING, LLC" className="h-12 md:h-14 ml-auto" /> : <OnyxWordmark />}
          </div>
        </div>
        <div className="bg-onyx-black text-white px-8 md:px-14 pt-12 pb-10">
          <h1 className="font-display font-light uppercase tracking-[0.12em] text-3xl md:text-5xl leading-[1.05]">{p.title}</h1>
          <div className="grid grid-cols-2 gap-8 mt-10">
            <div className="space-y-6">
              <div><div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-1.5">Prepared For</div><div className="font-display font-semibold md:text-base">{cust.company || cust.name || "—"}</div></div>
              <div><div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-1.5">Project</div><div className="font-display font-semibold md:text-base">{p.project_name || "—"}</div>{(p.project_address || s.address) && <div className="text-white/70 text-sm mt-1">{p.project_address || s.address}</div>}</div>
            </div>
            <div className="space-y-6">
              <div><div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-1.5">Prepared By</div><div className="font-display font-semibold md:text-base">{company}</div>{p.prepared_by && <div className="text-white/70 text-sm mt-1">{p.prepared_by}</div>}</div>
              <div><div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-1.5">Contact</div><div className="text-white/70 text-sm">{s.phone}</div><div className="text-white/70 text-sm">{s.email}</div></div>
            </div>
          </div>
          <div className="mt-10 text-[10px] tracking-[0.15em] text-white/40 uppercase">No. {num} &middot; {p.date || "—"} &middot; Valid until {p.expiration_date || "—"}</div>
        </div>
        <div className="bg-white px-8 md:px-14 py-4"><span className="font-display font-bold tracking-[0.25em] text-[11px] uppercase text-onyx-black">{s.tagline || "Architectural Railings. Built With Precision."}</span></div>
      </div>

      {/* 02 · Scope of Work */}
      <Panel>
        <RunHead company={company} num={num} /><SNum n="02" title="Scope of Work" />
        {(p.overview || p.project_description) && (<><SubK>Project Overview</SubK><p className="text-sm leading-relaxed text-onyx-charcoal/85 mb-8">{p.overview || p.project_description}</p></>)}
        <div className="grid md:grid-cols-2 gap-8">
          <div><SubK>Scope Includes</SubK><Dash items={p.scope_includes?.length ? p.scope_includes : p.scope_of_work} /></div>
          {quantities.length > 0 && (
            <div>
              <div className="border border-black/10 px-5 py-4" data-testid="proposal-quantities">
                <div className="text-[10px] tracking-[0.2em] uppercase text-onyx-metal mb-3">Quantities</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-[9px] tracking-[0.1em] uppercase text-onyx-metal border-b border-black/15"><th className="text-left py-1.5">Item</th><th className="text-left py-1.5">Qty</th></tr></thead>
                  <tbody>{quantities.filter((q) => q.item).map((q, i) => (<tr key={i} className="border-b border-black/5"><td className="py-2.5 font-semibold">{q.item}</td><td className="py-2.5 font-semibold">{q.qty}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {p.exclusions?.length > 0 && (<><SubK><span className="block mt-8">Exclusions</span></SubK><Dash items={p.exclusions} /></>)}
      </Panel>

      {/* 03 · Our Work */}
      {ourWork.length > 0 && (
        <Panel>
          <RunHead company={company} num={num} /><SNum n="03" title="Our Work" />
          <div className="grid md:grid-cols-2 gap-6">
            {ourWork.map((w, i) => (
              <div key={i}>
                <div className="h-52 bg-onyx-charcoal overflow-hidden">{w.image && <img src={fileUrl(w.image)} alt="" className="w-full h-full object-cover" />}</div>
                <div className="pt-3"><div className="font-display font-bold uppercase tracking-[0.06em] text-sm">{w.title}</div><div className="text-[9px] tracking-[0.15em] uppercase text-onyx-metal mt-1 mb-2">{w.location}</div><p className="text-sm text-onyx-charcoal/70 leading-relaxed">{w.caption}</p></div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* 04 · Investment */}
      <Panel>
        <RunHead company={company} num={num} /><SNum n="04" title="Investment" />
        <div className="bg-onyx-black text-white px-6 md:px-10">
          {[["Labor / Installation", money(labor), true], ["Mobilizations Included", p.mobilizations_included || "—"], ["Estimated Duration", p.estimated_duration || "—"], ["Crew Size", p.crew_size || "—"]].map(([k, v, big], i, arr) => (
            <div key={k} className={`flex items-center justify-between py-5 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
              <span className="text-[11px] tracking-[0.15em] uppercase text-white/70">{k}</span>
              {big ? <span className="font-display font-extrabold text-4xl">{v}</span> : <span className="font-display font-bold text-base">{v}</span>}
            </div>
          ))}
        </div>
        <div className="text-center pt-10 pb-2">
          <div className="text-[11px] tracking-[0.25em] uppercase text-onyx-metal mb-3">Proposal Total</div>
          <div className="font-display font-extrabold text-5xl md:text-6xl tracking-tight" data-testid="proposal-total">{money(p.total || labor)}</div>
        </div>
      </Panel>

      {/* 05 · Terms & Conditions */}
      <Panel>
        <RunHead company={company} num={num} /><SNum n="05" title="Terms & Conditions" />
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
          {terms.filter((t) => t.title).map((t, i) => (
            <div key={i}><div className="font-display font-bold uppercase tracking-[0.06em] text-[13px] mb-1.5">{t.title}</div><p className="text-sm text-onyx-charcoal/70 leading-relaxed">{t.body}</p></div>
          ))}
        </div>
      </Panel>

      {/* 06 · Acceptance (optional) */}
      {p.show_signature && (
        <Panel>
          <RunHead company={company} num={num} /><SNum n="06" title="Acceptance" />
          <p className="text-sm text-onyx-charcoal/70 leading-relaxed">The above prices, specifications and conditions are satisfactory and are hereby accepted.</p>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-12 mt-10">
            {["Name", "Company", "Signature", "Date"].map((l) => (
              <div key={l}><div className="border-t border-onyx-black mt-12 pt-2 text-[9px] tracking-[0.15em] uppercase text-onyx-metal">{l}</div></div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
};

export default ProposalDocument;
